/**
 * Caricamento di una risorsa remota, con gli stati che servono a non mentire.
 *
 * Il difetto che questo hook esiste per chiudere: un elenco inizializzato a
 * `[]` senza un indicatore di caricamento mostra «non c'è niente» mentre la
 * richiesta è ancora in volo. In un cruscotto di autorizzazione, «nessun
 * permesso» detto troppo presto è un'affermazione falsa e pericolosa. Qui i
 * dati partono da `null`: finché non arriva una risposta, chi rende sa che non
 * sa, e mostra uno `Skeleton` invece di uno stato vuoto.
 *
 * Il secondo difetto che chiude: una rilettura non azzera i dati. Dopo
 * un'operazione riuscita la pagina resta montata, i dati precedenti restano a
 * schermo e `refreshing` segnala che si sta aggiornando — invece di smontare
 * l'intera pagina e farla ricostruire da zero, perdendo scorrimento e fuoco.
 *
 * Non usa una libreria di gestione dati: `@tanstack/react-query` è stato
 * valutato e scartato (motivo nel file di sessione del 2026-09-20). Tre stati,
 * una rilettura e un aggiornamento mirato stanno in meno di cento righe e non
 * aggiungono peso al bundle, che è a sua volta un obiettivo di questa sessione.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { describeApiError, isForbidden } from '../errors';

/** Stato di una risorsa remota e comandi per agire su di essa. */
export interface Resource<T> {
  /** Dati letti; `null` finché la prima risposta non è arrivata. */
  data: T | null;
  /** True durante la prima lettura, quando `data` è ancora `null`. */
  loading: boolean;
  /** True durante una rilettura successiva, con i dati precedenti ancora a schermo. */
  refreshing: boolean;
  /** Messaggio dell'ultimo errore, `null` se l'ultima lettura è riuscita. */
  error: string | null;
  /** True se il backend ha risposto 403: non è un guasto, è una mancanza di permessi. */
  forbidden: boolean;
  /**
   * Rilegge la risorsa senza smontare nulla.
   *
   * @param options `silent: true` per le interrogazioni periodiche: la
   *   rilettura non accende `refreshing` e non toglie l'errore precedente
   *   prima della risposta, così lo schermo non lampeggia a ogni giro.
   * @returns Una promessa che si risolve a lettura conclusa, riuscita o meno.
   */
  reload: (options?: ReloadOptions) => Promise<void>;
  /**
   * Aggiorna i dati in locale, senza una nuova richiesta.
   *
   * Serve dopo una mutazione che restituisce già la riga aggiornata: evita il
   * giro di rete e, soprattutto, evita che la pagina si ricostruisca.
   *
   * @param update Funzione che riceve i dati correnti e restituisce i nuovi.
   */
  apply: (update: (current: T) => T) => void;
}

/** Opzioni di una rilettura. */
export interface ReloadOptions {
  /** True per una rilettura periodica che non deve mostrare l'attesa. */
  silent?: boolean;
}

/** Opzioni di `useResource`. */
export interface ResourceOptions {
  /**
   * Frase usata quando l'errore non viene dal backend.
   *
   * Dice sempre anche che cosa fare: un errore senza rimedio lascia l'utente
   * fermo.
   */
  fallbackMessage?: string;
  /**
   * Se `false`, la lettura non parte. Serve quando un parametro obbligatorio
   * non è ancora noto (per esempio l'identificativo di una risorsa padre).
   */
  enabled?: boolean;
}

/**
 * Legge una risorsa remota e ne espone lo stato.
 *
 * @param load Funzione che esegue la lettura. Va resa stabile con `useCallback`:
 *   un riferimento nuovo a ogni render farebbe ripartire la lettura in ciclo.
 * @param options Messaggio di ricaduta e attivazione condizionata.
 * @returns Lo stato della risorsa con i comandi di rilettura e aggiornamento.
 */
export function useResource<T>(load: () => Promise<T>, options: ResourceOptions = {}): Resource<T> {
  const { fallbackMessage, enabled = true } = options;

  const [data, setData] = useState<T | null>(null);
  const [pending, setPending] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);

  // Vero finché il componente è montato: una risposta che arriva dopo lo
  // smontaggio non deve chiamare `setState`.
  const alive = useRef(true);
  // Numero della lettura corrente: una risposta lenta di una lettura vecchia
  // non deve sovrascrivere il risultato di una più recente.
  const generation = useRef(0);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const reload = useCallback(async (options: ReloadOptions = {}): Promise<void> => {
    if (!enabled) {
      return;
    }
    generation.current += 1;
    const mine = generation.current;
    if (options.silent !== true) {
      setPending(true);
      // L'errore precedente sparisce all'inizio del tentativo: chi ha premuto
      // «Riprova» deve vedere che sta succedendo qualcosa, non l'errore vecchio.
      setError(null);
      setForbidden(false);
    }
    try {
      const loaded = await load();
      if (alive.current && mine === generation.current) {
        setData(loaded);
        setError(null);
        setForbidden(false);
      }
    } catch (caught) {
      if (alive.current && mine === generation.current) {
        if (isForbidden(caught)) {
          setForbidden(true);
          setError(null);
        } else {
          setError(describeApiError(caught, fallbackMessage));
          setForbidden(false);
        }
      }
    } finally {
      if (alive.current && mine === generation.current) {
        setPending(false);
      }
    }
  }, [enabled, fallbackMessage, load]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    void reload();
  }, [enabled, reload]);

  const apply = useCallback((update: (current: T) => T): void => {
    setData((current) => (current === null ? current : update(current)));
  }, []);

  return {
    data,
    loading: pending && data === null && !forbidden && error === null,
    refreshing: pending && data !== null,
    error,
    forbidden,
    reload,
    apply,
  };
}
